import React, { Component } from 'react';
import queryString from 'query-string';
import _ from 'underscore';
import {
  contentfulItemAllowsExperience,
  visibilityAllowsExperience,
} from '../../utils/visibility';

// ---------- helpers ----------
function safeStr(v) {
  if (v === null || v === undefined) return '';
  return String(v);
}

function includesCI(haystack, needle) {
  const h = safeStr(haystack).toLowerCase();
  const n = safeStr(needle).toLowerCase();
  if (!n) return false;
  return h.includes(n);
}

function visibilityAllowsInternal(value) {
  // Internal-only users are using this search, so we can safely include internal-visible items.
  // Keep permissive because Contentful values vary: "internal", "Internal", null, etc.
  return visibilityAllowsExperience(value, 'internal', { allowEmpty: true });
}

function normalizeSalesforceArticles(raw) {
  // raw can be:
  // - { articles: [...] }
  // - JSON string
  // - [...]
  if (!raw) return [];
  let parsed = raw;

  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return [];
    }
  }

  if (Array.isArray(parsed)) return parsed;
  if (parsed.articles && Array.isArray(parsed.articles)) return parsed.articles;
  return [];
}

// ---------- renderers ----------
function NewsResults({ query, contentfulNews, salesforceNews }) {
  const q = safeStr(query).trim();
  if (!q) {
    return (
      <div className="text black">
        <h1 className="black">
          <em>0</em> news results <span className="search-term black">for ""</span>
        </h1>
      </div>
    );
  }

  const cfHits = _.filter(contentfulNews, (n) => {
    return (
      includesCI(n.newsTitle, q) ||
      includesCI(n.description, q) ||
      includesCI(n.department?.name, q)
    );
  });

  const sfHits = _.filter(salesforceNews, (a) => {
    if (!a) return false;
    if (includesCI(a.email_header, q)) return true;
    if (includesCI(a.name, q)) return true;

    // Search in subjects + bodies (can be HTML)
    const stories = Array.isArray(a.stories) ? a.stories : [];
    for (let i = 0; i < stories.length; i++) {
      if (includesCI(stories[i]?.subject, q)) return true;
      if (includesCI(stories[i]?.body, q)) return true;
    }
    return false;
  });

  const total = cfHits.length + sfHits.length;

  return (
    <div className="text black">
      <h1 className="black">
        <em>{total}</em> news results
        <span className="search-term black">
          <span> for </span>"{q}"
        </span>
      </h1>

      {/* Contentful news => /cubs-news-departments/:route */}
      {cfHits.map((n, idx) => (
        <ol key={`cf-news-${idx}`}>
          <h2 className="black">
            <a href={`/cubs-news-departments/${n.route}`}>{n.newsTitle}</a>
          </h2>
          <div className="text-meta" role="contentinfo">
            <ul className="text-data black">
              <li>{safeStr(n.date)}</li>
              <li>Category: News</li>
            </ul>
          </div>
        </ol>
      ))}

      {/* Salesforce news => /cubs-news/:id */}
      {sfHits.map((a, idx) => (
        <ol key={`sf-news-${idx}`}>
          <h2 className="black">
            <a href={`/cubs-news/${a.id}`}>{a.email_header || a.name || a.id}</a>
          </h2>
          <div className="text-meta" role="contentinfo">
            <ul className="text-data black">
              <li>{safeStr(a.publish_date).slice(0, 10)}</li>
              <li>Category: News</li>
            </ul>
          </div>
        </ol>
      ))}
    </div>
  );
}

function FileResults({ query, filePages, deptPages }) {
  const q = safeStr(query).trim();
  if (!q) {
    return (
      <div className="text black">
        <h1 className="black">
          <em>0</em> file results <span className="search-term black">for ""</span>
        </h1>
      </div>
    );
  }

  // Flatten all documents from all listing pages (this fixes the old bug: it only searched first 3 pages)
  const allDocs = [];
  (filePages || []).forEach((p) => {
    const docs = Array.isArray(p.filesAndDocuments) ? p.filesAndDocuments : [];
    docs.forEach((d) => allDocs.push({ pageRoute: p.route, doc: d }));
  });

  // Dept pages: quickLinks + popularDocuments treated as File results (no Link category)
  const allQuickLinks = [];
  const allPopularDocs = [];
  (deptPages || []).forEach((p) => {
    const quick = Array.isArray(p.quickLinks) ? p.quickLinks : [];
    quick.forEach((ql) => allQuickLinks.push({ pageRoute: p.route, link: ql }));

    const pops = Array.isArray(p.popularDocuments) ? p.popularDocuments : [];
    pops.forEach((pd) => allPopularDocs.push({ pageRoute: p.route, pop: pd }));
  });

  const docHits = _.filter(allDocs, (x) => {
    const d = x.doc;
    return (
      includesCI(d.title, q) ||
      includesCI(d.department, q) ||
      includesCI(d.uploadDate, q) ||
      includesCI(x.pageRoute, q)
    );
  });

  const quickHits = _.filter(allQuickLinks, (x) => {
    return includesCI(x.link.text, q) || includesCI(x.link.route, q) || includesCI(x.pageRoute, q);
  });

  const popHits = _.filter(allPopularDocs, (x) => {
    const pd = x.pop;
    return (
      includesCI(pd.text, q) ||
      includesCI(pd.fileLink?.title, q) ||
      includesCI(pd.fileLink?.description, q) ||
      includesCI(pd.fileLink?.url, q) ||
      includesCI(x.pageRoute, q)
    );
  });

  const total = docHits.length + quickHits.length + popHits.length;

  return (
    <div className="text black">
      <h1 className="black">
        <em>{total}</em> file results
        <span className="search-term black">
          <span> for </span>"{q}"
        </span>
      </h1>

      {/* Documents */}
      {docHits.map((x, idx) => {
        const d = x.doc;
        const url = d?.fileUpload?.url;
        return (
          <ol key={`doc-${idx}`}>
            <h2 className="black">
              {url ? <a href={url}>{d.title}</a> : <span>{d.title}</span>}
            </h2>
            <div className="text-meta" role="contentinfo">
              <ul className="text-data black">
                <li>{safeStr(d.uploadDate)}</li>
                <li>Category: File</li>
              </ul>
            </div>
          </ol>
        );
      })}

      {/* Popular documents */}
      {popHits.map((x, idx) => {
        const pd = x.pop;
        const url = pd?.fileLink?.url;
        const label = pd?.fileLink?.title || pd?.text || url || 'Document';
        return (
          <ol key={`pop-${idx}`}>
            <h2 className="black">{url ? <a href={url}>{label}</a> : <span>{label}</span>}</h2>
            <div className="text-meta" role="contentinfo">
              <ul className="text-data black">
                <li>Category: File</li>
              </ul>
            </div>
          </ol>
        );
      })}

      {/* Quick links (internal pages) */}
      {quickHits.map((x, idx) => {
        const l = x.link;
        const url = l?.route || '#';
        const label = l?.text || l?.route || 'Link';
        return (
          <ol key={`quick-${idx}`}>
            <h2 className="black">
              <a href={url.startsWith('/') ? url : `/${url}`}>{label}</a>
            </h2>
            <div className="text-meta" role="contentinfo">
              <ul className="text-data black">
                <li>Category: File</li>
              </ul>
            </div>
          </ol>
        );
      })}
    </div>
  );
}

function AssociateResults({ query, associates }) {
  const q = safeStr(query).trim();
  if (!q) {
    return (
      <div className="text black">
        <h1 className="black">
          <em>0</em> associates found <span className="search-term black">for ""</span>
        </h1>
      </div>
    );
  }

  const hits = _.filter(associates || [], (a) => includesCI(a.associate, q));
  return (
    <div className="text black">
      <h1 className="black">
        <em>{hits.length}</em> associates found
        <span className="search-term black">
          <span> for </span>"{q}"
        </span>
      </h1>

      {hits.map((a, idx) => (
        <ol key={`assoc-${idx}`}>
          <h2 className="black">{a.associate}</h2>
          <div className="text-meta" role="contentinfo">
            <ul className="text-data black">
              <li>{safeStr(a.title)}</li>
              <li>{safeStr(a.phone)}</li>
              <li>{safeStr(a.email)}</li>
            </ul>
          </div>
        </ol>
      ))}
    </div>
  );
}

// ---------- main component ----------
class SearchResults extends Component {
  state = {
    loaded: false,
    query: '',
    associates: [],
    salesforceNews: [],
    error: null
  };

  componentDidMount() {
    const value = queryString.parse(this.props.location?.search || '');
    const query = safeStr(value.queryStr || '').trim();

    // Load associates + salesforce news in parallel
    Promise.all([
      fetch('/employee', { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .catch(() => ({ associates: [] })),

      fetch('/news', { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .catch(() => ({}))
    ])
      .then(([assocPayload, sfPayload]) => {
        const associates = Array.isArray(assocPayload?.associates) ? assocPayload.associates : [];
        associates.sort((a, b) => safeStr(a.associate).localeCompare(safeStr(b.associate)));

        const salesforceNews = normalizeSalesforceArticles(sfPayload);

        this.setState({
          loaded: true,
          query,
          associates,
          salesforceNews,
          error: null
        });
      })
      .catch((err) => {
        this.setState({
          loaded: true,
          query,
          associates: [],
          salesforceNews: [],
          error: err?.message || String(err)
        });
      });
  }

  render() {
    const q = this.props.searchPageQuery;
    if (!q) return <div>Loading…</div>;
    if (!this.state.loaded) return <div>Loading…</div>;

    const contentfulNewsAll = Array.isArray(q.newsItems) ? q.newsItems : [];
    const contentfulNews = contentfulNewsAll.filter((n) => {
      return contentfulItemAllowsExperience(n, 'internal', { allowEmpty: true });
    });

    const filePagesAll = Array.isArray(q.genericPageListingOfFiles) ? q.genericPageListingOfFiles : [];
    const filePages = filePagesAll.map((p) => ({
      ...p,
      filesAndDocuments: (Array.isArray(p.filesAndDocuments) ? p.filesAndDocuments : []).filter((d) =>
        visibilityAllowsInternal(d.visibilityValue)
      )
      // seasonalFilesAndDocuments intentionally ignored (search is internal-only)
    }));

    const deptPagesAll = Array.isArray(q.genericPageWithNews) ? q.genericPageWithNews : [];
    const deptPages = deptPagesAll.filter((p) => {
      // Keep “active pages”: require route
      if (!p?.route) return false;
      // department visibilityValue can exist on p.department
      if (p.department && !visibilityAllowsInternal(p.department)) return false;
      return true;
    });

    return (
      <section className="search-results">
        {this.state.error ? (
          <div style={{ padding: 12 }}>
            <b>Search data error:</b> {this.state.error}
          </div>
        ) : null}

        <div className="row">
          <div className="col md-6">
            <div className="section-header">
              <h2 className="section-title">Cubs Results</h2>
            </div>

            <div className="section-content cubs-results">
              <h3 className="black" style={{ marginTop: 0 }}>News</h3>
              <NewsResults
                query={this.state.query}
                contentfulNews={contentfulNews}
                salesforceNews={this.state.salesforceNews}
              />

              <hr />

              <h3 className="black">Files</h3>
              <FileResults query={this.state.query} filePages={filePages} deptPages={deptPages} />
            </div>
          </div>

          <div className="col md-6">
            <div className="section-header">
              <h2 className="section-title">Associate Search Results</h2>
            </div>

            <div className="section-content cubs-results">
              <AssociateResults query={this.state.query} associates={this.state.associates} />
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default SearchResults;
